/**
 * The three tier marks. Sprout, anvil, crown — a progression that does not
 * reuse the nav (wheat is ingredients, flask is recipes) or the wordmark mug.
 *
 * Lives in the UI layer, not `lib/plan.ts`: that module is imported by the
 * Worker and cannot pull React.
 */

import { AnvilIcon, CrownIcon, type LucideIcon, SproutIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import type { Plan } from '@/lib/plan';
import { planName } from '@/lib/plan';
import { cn } from '@/lib/utils';

export const PLAN_ICONS: Readonly<Record<Plan, LucideIcon>> = {
  trial: SproutIcon,
  brewmaster: AnvilIcon,
  beerwright: CrownIcon,
};

export function PlanIcon({
  plan,
  className,
}: {
  readonly plan: Plan;
  readonly className?: string | undefined;
}) {
  const Icon = PLAN_ICONS[plan];
  return <Icon className={cn('size-3.5 shrink-0', className)} aria-hidden="true" />;
}

/** Icon plus the tier name (or a longer summary that starts with it). */
export function PlanName({
  plan,
  children,
  className,
  iconClassName,
}: {
  readonly plan: Plan;
  readonly children?: ReactNode | undefined;
  readonly className?: string | undefined;
  readonly iconClassName?: string | undefined;
}) {
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1.5', className)}>
      <PlanIcon plan={plan} className={iconClassName} />
      <span className="min-w-0 truncate">{children ?? planName(plan)}</span>
    </span>
  );
}
